import React, { FC, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { localized, localizedInterpolation } from 'shared/state/i18n/i18n';
import { WebAppState } from 'web/state/store.web';
import { WhiteText } from 'web/view/components/Text/white-text';
import PageContainer from 'web/view/components/page-container/page-container';
import { SkioldButton } from 'web/view/components/skiold-components/skiold-button/skiold-button';
import { ViewWeb } from 'web/view/components/utils/web-view';
import { GetAnimalListPdf, GetDadMomTablePdf } from 'web/web-helpers/pdf-helper/animal-list-helper';
import { Heading } from '../../utils/heading';
import { BarChart } from '../../graph/bar-graph/bar-chart';
import { RawAxisProps, SeriesProps } from '@devexpress/dx-react-chart';
import moment from 'moment';
import { DashboardTableModelItem } from 'shared/api/api';
import { Colors } from 'shared/assets/root-assets/colors';

interface PropsFromParent {
	closeModal: () => void;
}
const getDashboardMatingItemsByWeek = (
	listObejct: { [key: string]: DashboardTableModelItem } | undefined,
	object: any,
	propKey: string,
) => {
	// get data for each dashboard item, and separate by week and propKey
	listObejct &&
		Object.keys(listObejct).forEach(key => {
			const val = listObejct[key];
			if (val.matingBatch?.matingPeriodStart) {
				if(!val.value){
					return;
				}
				const date = moment(val.matingBatch?.matingPeriodStart);

				const key = `${val.matingBatch.batchNumber } (${date.isoWeek()}-${date.year()})`;
				if (!object[key]) {
					object[key] = {};
				}
				object[key][propKey] = val.value ? +val.value : 0;
				object[key].date = val.matingBatch?.matingPeriodStart;
			}
		});
};

export const TotalMatingsBarGraph: FC<PropsFromParent> = React.memo(({ closeModal }) => {
	// Get dashboard data
	const dashboardMatingItems = useSelector((state: WebAppState) =>
		state.dashboardOverview.data &&
		state.dashboardOverview.data.dashboardTableData &&
		state.dashboardOverview.data.dashboardTableData['MatingDashboard']
			? state.dashboardOverview.data.dashboardTableData['MatingDashboard']
			: [],
	);

	// Set bar series
	const series = useMemo(() => {
		return [
			{
				argumentField: 'week',
				valueField: 'sows',
				name: 'sows',
				color: Colors.darkGreen,
				key: 'sows',
			} as SeriesProps,
			{
				argumentField: 'week',
				valueField: 'youngFemales',
				name: 'youngFemales',
				color: Colors.lightGreen,
				key: 'youngFemales',
			} as SeriesProps,
			{
				argumentField: 'week',
				valueField: 'returnSows',
				name: 'returnSows',
				color: Colors.darkRed,
				key: 'returnSows',
			} as SeriesProps,
		];
	}, []);

	//  set YAxis
	const yAxis = useMemo(() => {
		return [{ position: 'left' } as RawAxisProps];
	}, []);

	// generate bar chart data
	const data = useMemo(() => {
		// separate data by dashboard item

		let returnSows = dashboardMatingItems.find(x => x.name === 'ReturnSows')?.data;
		let youngFemales = dashboardMatingItems.find(x => x.name === 'YoungAnimalMatings')?.data;
		let sows = dashboardMatingItems.find(x => x.name === 'TotalMatings')?.data;
		// create object with week as key
		let objectData = {};

		// get data for each dashboard item
		getDashboardMatingItemsByWeek(sows, objectData, 'sows');
		getDashboardMatingItemsByWeek(returnSows, objectData, 'returnSows');
		getDashboardMatingItemsByWeek(youngFemales, objectData, 'youngFemales');

		// map object to array
		let resultArray = Object.keys(objectData).map(key => {
			let week = objectData[key];
			if (week) {
				week.week = key;
				week.sows -= week.youngFemales;
				week.sows -= week.returnSows;
			}
			return week;
		});
		return resultArray.slice().sort((a, b) => a.date - b.date);
	}, [dashboardMatingItems]);

	return (
		<PageContainer>
			<Heading text={localized('Matings')} />
			<ViewWeb className="default-spacing">
				<ViewWeb className="default-close-pos">
					<SkioldButton theme="grey" onPress={closeModal} title={localized('Close')} />
				</ViewWeb>
				<ViewWeb className="flex-row">
					<ViewWeb className="vertical-legend-title">
						<div className="rotate-container">
							<WhiteText className="header-white-text rotate-270">
								{localized('MatingsPerBatchPcs')}
							</WhiteText>
						</div>
					</ViewWeb>
					<ViewWeb className="flexOne">
						<BarChart
							barSeries={series}
							chartData={data}
							valueAxis={yAxis}
							title=""
							stack={true}
							keyTranslator={localized}
							zoomPan="both"
							verticalArgumentLabels={true}
						/>
						<WhiteText className="header-white-text">{localized('matingBatch')}</WhiteText>
					</ViewWeb>
				</ViewWeb>
			</ViewWeb>
		</PageContainer>
	);
});
